import { useMemo, useState, useEffect } from "react"
import { useParams, useNavigate, useLocation } from "react-router-dom"
import { PaginationState } from "@tanstack/react-table"
import { ALLOWED_ROLES_BY_ROUTE } from "../../common/constants"
import BrandActivities from "../../common/ui/brand-details/activities"
import BrandDetails from "../../common/ui/brand-details/details"
import BrandDetailsHeader from "../../common/ui/brand-details/header"
import BrandDetailsIntegrations from "../../common/ui/integrations/integrationTable"
import BrandDetailsLeads from "../../common/ui/brand-details/leads"
import BrandDetailsLoanOfficers from "../../common/ui/brand-details/loan-officers/BrandDetailsLoanOfficers"
import EngagementsTable from "../../common/ui/engagements/table"
import PricingLogs from "../../common/ui/pricing-logs"
import PricingLogCardDetails from "../../common/ui/pricing-logs/pricing-log-card-details"
import ManageBrand from "../../common/ui/brands/manage"
import ManageIntegration from "../../common/ui/integrations/manage"
import BBDialog from "../../common/ui/dialog"
import Tabs from "../../common/ui/tabs"
import { RequireRole } from "../../common/validation"
import {
  useFetchIntegrationsQuery,
  useFetchLeadsByBrandQuery,
  useFetchBrandQuery,
  useFetchOrgDetailsQuery,
  useGetIntegrationDetailsQuery,
  useGetStatusQuery
} from "../../redux/queries"
import { Integration } from "../../types"
import { If } from "../../common/ui"
import { BrandDetailsSkeleton } from "../../common/ui/skeletons"

const BrandDetailsPage = () => {
  const { brandId: id, orgId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()

  const tabParam =
    new URLSearchParams(location.search).get("tab") || "brand-details"

  const { data: orgDetails } = useFetchOrgDetailsQuery(
    { id: orgId ?? "" },
    { skip: !orgId }
  )
  const { data: brand, isFetching } = useFetchBrandQuery(id ?? "", {
    skip: !id
  })

  const { data: leads, isLoading: isLoadingLeads } = useFetchLeadsByBrandQuery(
    id ?? "",
    { skip: !id }
  )

  const [integrationsPagination, setIntegrationsPagination] =
    useState<PaginationState>({
      pageIndex: 0,
      pageSize: 10
    })

  const [selectedIntegrationId, setSelectedIntegrationId] = useState<
    string | null
  >(null)
  const [selectedPricingLogId, setSelectedPricingLogId] = useState<
    string | null
  >(null)

  const { data: integrationsResponse, isLoading: isLoadingIntegrationsAll } =
    useFetchIntegrationsQuery(
      {
        org_id: brand?.org_id,
        brand_id: id as string,
        limit: integrationsPagination.pageSize,
        offset:
          integrationsPagination.pageIndex * integrationsPagination.pageSize
      },
      {
        skip: !brand?.org_id,
        refetchOnMountOrArgChange: true
      }
    )

  const integrations = useMemo(
    () =>
      (integrationsResponse?.results as Integration[])?.filter(
        ({ brand_id }) => brand_id === id
      ) || [],
    [id, integrationsResponse?.results]
  )

  const { data: integration, isLoading: isLoadingIntegration } =
    useGetIntegrationDetailsQuery(selectedIntegrationId ?? "", {
      skip: !selectedIntegrationId
    })

  const { data: statusData, isLoading: isLoadingStatus } = useGetStatusQuery(
    { integrationID: selectedIntegrationId ?? "" },
    { skip: !selectedIntegrationId }
  )

  const [manageBrandVisible, setManageBrandVisible] = useState(false)
  const [deleteBrandVisible, setDeleteBrandVisible] = useState(false)

  const [tab, setTab] = useState(tabParam)

  const onChangeTab = (newTab: string) => {
    setTab(newTab)
    navigate(`${location.pathname}?tab=${newTab}`, { replace: true })
  }

  // Sync tab with browser back/forward navigation
  useEffect(() => {
    const currentTabParam =
      new URLSearchParams(location.search).get("tab") || "brand-details"
    if (currentTabParam !== tab) {
      setTab(currentTabParam)
    }
  }, [location.search, tab])

  const tabContent = {
    "brand-details": brand && <BrandDetails brand={brand} />,
    activity: brand && <BrandActivities brand={brand} />,
    "loan-officers": <BrandDetailsLoanOfficers brandId={id} />,
    leads: (
      <BrandDetailsLeads
        data={leads ?? []}
        isLoading={isLoadingLeads}
        brandId={id}
      />
    ),
    subscriptions: (
      <div className="p-6">
        <EngagementsTable
          brandId={id}
          brandName={brand?.name}
          showStats={false}
          showDateFilter={false}
        />
      </div>
    ),
    integrations: (
      <BrandDetailsIntegrations
        data={integrations ?? []}
        isLoading={isLoadingIntegrationsAll}
        onSelect={setSelectedIntegrationId}
        totalCount={integrationsResponse?.total}
        pagination={integrationsPagination}
        setPagination={setIntegrationsPagination}
      />
    ),
    "pricing-log": (
      <PricingLogs id={{ bank_id: id }} onSelect={setSelectedPricingLogId} />
    )
  }

  if (!brand?.id || !id)
    return (
      <If
        condition={!isFetching}
        then={<div className="p-6">Brand not found</div>}
        else={<BrandDetailsSkeleton />}
      />
    )

  return (
    <RequireRole allowedRoles={ALLOWED_ROLES_BY_ROUTE.brands}>
      <div className="h-full bg-white">
        <BrandDetailsHeader
          brand={brand}
          onClickDelete={() => setDeleteBrandVisible(true)}
          onClickEdit={() => setManageBrandVisible(true)}
          breadcrumbs={
            orgDetails
              ? [
                  { name: "Organizations", href: "/organizations" },
                  {
                    name: orgDetails.name,
                    href: `/organizations/${orgDetails.id}`
                  },
                  { name: brand.name, href: "#", current: true }
                ]
              : undefined
          }
        />
        <div>
          <Tabs
            className="px-6"
            currentTab={tab}
            tabs={[
              { id: "brand-details", label: "Brand Details" },
              { id: "activity", label: "Activity" },
              { id: "loan-officers", label: "Loan Officers" },
              { id: "leads", label: "Leads" },
              { id: "subscriptions", label: "Subscriptions" },
              { id: "integrations", label: "Brand Integrations" },
              { id: "pricing-log", label: "Pricing Log" }
            ]}
            onChange={onChangeTab}
          />
          <div className="bg-white">
            {tabContent[tab as keyof typeof tabContent]}
          </div>
        </div>
        <ManageBrand
          open={manageBrandVisible}
          data={brand}
          isInSidePanel
          onClose={() => setManageBrandVisible(false)}
        />
        <BBDialog
          open={deleteBrandVisible}
          onClose={() => setDeleteBrandVisible(false)}
          title="Delete brand"
          body="By doing this all the information stored in the system about this brand will be deleted. Are you sure?"
          okLabel="Yes, Delete"
          cancelLabel="Cancel"
          onOk={() => {}}
          onCancel={() => setDeleteBrandVisible(false)}
        />
        <ManageIntegration
          open={!!selectedIntegrationId}
          isCreate={false}
          integrationId={selectedIntegrationId ?? undefined}
          integration={integration}
          statusData={statusData}
          isLoadingStatus={isLoadingStatus}
          isLoadingIntegration={isLoadingIntegration}
          onClose={() => setSelectedIntegrationId(null)}
        />
        <PricingLogCardDetails
          open={!!selectedPricingLogId}
          onClose={() => setSelectedPricingLogId(null)}
          data={
            selectedPricingLogId
              ? ({ id: selectedPricingLogId } as any)
              : undefined
          }
        />
      </div>
    </RequireRole>
  )
}

export default BrandDetailsPage
